import { create } from "zustand";
import { persist } from "zustand/middleware";
import { produce } from "immer";

// Entry types
export type DebugEntryType =
  | "request"
  | "response"
  | "event"
  | "subscribe"
  | "unsubscribe"
  | "serverLog"
  | "consoleLog";

export type DebugEntry = {
  id: string;
  timestamp: number;
  type: DebugEntryType;
  topic: string;
  name: string;
  method?: string;
  requestId?: string;
  data: any;
  status?: number;
  duration?: number;
  error?: string;
  // Server log specific fields
  segment?: string;
  level?: string;
};

// Filter group types - includes filter state per group
export type FilterGroup = {
  id: string;
  name: string;
  includes: string[];
  excludes: string[];
  types: DebugEntryType[];
  topic: string;
  search: string;
  // Server log subscription settings
  serverLogsEnabled: boolean;
  serverLogSegments: string[]; // e.g. ["livekit", "room"] or ["*"] for all
  serverLogLevels: string[]; // e.g. ["info", "warning"] or ["*"] for all
  // Console log capture settings
  consoleLogsEnabled: boolean;
};

export type DisplayMode = "all" | "visible" | "included" | "excluded";

type GatewayDebuggerState = {
  // Entries state
  entries: DebugEntry[];
  maxEntries: number;
  isEnabled: boolean;
  combinedMode: boolean;

  // Filter groups state
  filterGroups: FilterGroup[];
  activeGroupId: string;
  displayMode: DisplayMode;
};

type GatewayDebuggerActions = {
  // Entry actions
  addEntry: (entry: Omit<DebugEntry, "id" | "timestamp">) => void;
  clearEntries: () => void;
  setEnabled: (enabled: boolean) => void;
  setCombinedMode: (combined: boolean) => void;

  // Filter group actions
  createGroup: (name: string) => void;
  deleteGroup: (id: string) => void;
  renameGroup: (id: string, name: string) => void;
  setActiveGroup: (id: string) => void;
  setGroupFilter: (
    filter: Partial<Pick<FilterGroup, "types" | "topic" | "search">>,
  ) => void;
  setServerLogSettings: (settings: {
    enabled?: boolean;
    segments?: string[];
    levels?: string[];
  }) => void;
  setConsoleLogsEnabled: (enabled: boolean) => void;
  addToIncludes: (pattern: string) => void;
  addToExcludes: (pattern: string) => void;
  removeFromIncludes: (pattern: string) => void;
  removeFromExcludes: (pattern: string) => void;
  setDisplayMode: (mode: DisplayMode) => void;
  setMaxEntries: (max: number) => void;
};

const createEntryId = () =>
  `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const createGroupId = () => crypto.randomUUID();

const createDefaultGroup = (): FilterGroup => ({
  id: createGroupId(),
  name: "Main",
  includes: [],
  excludes: [],
  types: [],
  topic: "",
  search: "",
  serverLogsEnabled: false,
  serverLogSegments: ["*"],
  serverLogLevels: ["*"],
  consoleLogsEnabled: false,
});

const DEFAULT_GROUP = createDefaultGroup();

export const useGatewayDebuggerStore = create<
  GatewayDebuggerState & GatewayDebuggerActions
>()(
  persist(
    (set, get) => {
      const update = (updater: (state: GatewayDebuggerState) => void) =>
        set(produce(updater));

      return {
        // Initial entries state
        entries: [],
        maxEntries: 100,
        isEnabled: true,
        combinedMode: true,

        // Initial filter groups state - start with default group
        filterGroups: [DEFAULT_GROUP],
        activeGroupId: DEFAULT_GROUP.id,
        displayMode: "all",

        // Entry actions
        addEntry: (entry) =>
          update((state) => {
            if (!state.isEnabled) return;

            const newEntry: DebugEntry = {
              ...entry,
              id: createEntryId(),
              timestamp: Date.now(),
            };

            state.entries = [newEntry, ...state.entries].slice(
              0,
              state.maxEntries,
            );
          }),

        clearEntries: () =>
          update((state) => {
            state.entries = [];
          }),

        setEnabled: (isEnabled) =>
          update((state) => {
            state.isEnabled = isEnabled;
          }),

        setCombinedMode: (combinedMode) =>
          update((state) => {
            state.combinedMode = combinedMode;
          }),

        // Filter group actions
        setGroupFilter: (filter) =>
          update((state) => {
            const group = state.filterGroups.find(
              (g) => g.id === state.activeGroupId,
            );
            if (group) {
              if (filter.types !== undefined) group.types = filter.types;
              if (filter.topic !== undefined) group.topic = filter.topic;
              if (filter.search !== undefined) group.search = filter.search;
            }
          }),

        setServerLogSettings: (settings) =>
          update((state) => {
            const group = state.filterGroups.find(
              (g) => g.id === state.activeGroupId,
            );
            if (group) {
              if (settings.enabled !== undefined)
                group.serverLogsEnabled = settings.enabled;
              if (settings.segments !== undefined)
                group.serverLogSegments = settings.segments;
              if (settings.levels !== undefined)
                group.serverLogLevels = settings.levels;
            }
          }),

        setConsoleLogsEnabled: (enabled) =>
          update((state) => {
            const group = state.filterGroups.find(
              (g) => g.id === state.activeGroupId,
            );
            if (group) {
              group.consoleLogsEnabled = enabled;
            }
          }),

        createGroup: (name) =>
          update((state) => {
            const newGroup: FilterGroup = {
              id: createGroupId(),
              name,
              includes: [],
              excludes: [],
              types: [],
              topic: "",
              search: "",
              serverLogsEnabled: false,
              serverLogSegments: ["*"],
              serverLogLevels: ["*"],
              consoleLogsEnabled: false,
            };
            state.filterGroups.push(newGroup);
            state.activeGroupId = newGroup.id;
          }),

        deleteGroup: (id) =>
          update((state) => {
            const index = state.filterGroups.findIndex((g) => g.id === id);
            if (index === -1) return;

            state.filterGroups.splice(index, 1);

            // If deleted active group, switch to first available or create new
            if (state.activeGroupId === id) {
              if (state.filterGroups.length > 0) {
                state.activeGroupId = state.filterGroups[0].id;
              } else {
                const defaultGroup = createDefaultGroup();
                state.filterGroups.push(defaultGroup);
                state.activeGroupId = defaultGroup.id;
              }
            }
          }),

        renameGroup: (id, name) =>
          update((state) => {
            const group = state.filterGroups.find((g) => g.id === id);
            if (group) {
              group.name = name;
            }
          }),

        setActiveGroup: (id) =>
          update((state) => {
            state.activeGroupId = id;
          }),

        addToIncludes: (pattern) =>
          update((state) => {
            const group = state.filterGroups.find(
              (g) => g.id === state.activeGroupId,
            );
            if (group && !group.includes.includes(pattern)) {
              group.includes.push(pattern);
              // Remove from excludes if present
              const excludeIndex = group.excludes.indexOf(pattern);
              if (excludeIndex !== -1) {
                group.excludes.splice(excludeIndex, 1);
              }
            }
          }),

        addToExcludes: (pattern) =>
          update((state) => {
            const group = state.filterGroups.find(
              (g) => g.id === state.activeGroupId,
            );
            if (group && !group.excludes.includes(pattern)) {
              group.excludes.push(pattern);
              // Remove from includes if present
              const includeIndex = group.includes.indexOf(pattern);
              if (includeIndex !== -1) {
                group.includes.splice(includeIndex, 1);
              }
            }
          }),

        removeFromIncludes: (pattern) =>
          update((state) => {
            const group = state.filterGroups.find(
              (g) => g.id === state.activeGroupId,
            );
            if (group) {
              const index = group.includes.indexOf(pattern);
              if (index !== -1) {
                group.includes.splice(index, 1);
              }
            }
          }),

        removeFromExcludes: (pattern) =>
          update((state) => {
            const group = state.filterGroups.find(
              (g) => g.id === state.activeGroupId,
            );
            if (group) {
              const index = group.excludes.indexOf(pattern);
              if (index !== -1) {
                group.excludes.splice(index, 1);
              }
            }
          }),

        setDisplayMode: (displayMode) =>
          update((state) => {
            state.displayMode = displayMode;
          }),

        setMaxEntries: (max) =>
          update((state) => {
            state.maxEntries = max;
            // Trim entries if needed
            if (state.entries.length > max) {
              state.entries = state.entries.slice(-max);
            }
          }),
      };
    },
    {
      name: "gateway-debugger-store",
      partialize: (state) => ({
        // Only persist filter groups and settings, not entries
        filterGroups: state.filterGroups,
        activeGroupId: state.activeGroupId,
        displayMode: state.displayMode,
        combinedMode: state.combinedMode,
        isEnabled: state.isEnabled,
        maxEntries: state.maxEntries,
      }),
      merge: (persistedState, currentState) => {
        const persisted = persistedState as Partial<GatewayDebuggerState>;

        // Ensure we always have at least one group
        let filterGroups = persisted?.filterGroups ?? currentState.filterGroups;
        let activeGroupId =
          persisted?.activeGroupId ?? currentState.activeGroupId;

        if (!filterGroups || filterGroups.length === 0) {
          const defaultGroup = createDefaultGroup();
          filterGroups = [defaultGroup];
          activeGroupId = defaultGroup.id;
        } else {
          // Migrate old groups: add missing fields
          filterGroups = filterGroups.map((g) => ({
            ...g,
            types: g.types ?? [],
            topic: g.topic ?? "",
            search: g.search ?? "",
            includes: g.includes ?? [],
            excludes: g.excludes ?? [],
            serverLogsEnabled: g.serverLogsEnabled ?? false,
            serverLogSegments: g.serverLogSegments ?? ["*"],
            serverLogLevels: g.serverLogLevels ?? ["*"],
          }));

          if (
            !activeGroupId ||
            !filterGroups.find((g) => g.id === activeGroupId)
          ) {
            activeGroupId = filterGroups[0].id;
          }
        }

        return {
          ...currentState,
          ...persisted,
          filterGroups,
          activeGroupId,
        };
      },
    },
  ),
);

// Helper to get active filter group
export const getActiveFilterGroup = (
  state: GatewayDebuggerState,
): FilterGroup | undefined => {
  return state.filterGroups.find((g) => g.id === state.activeGroupId);
};

// Helper to check if entry matches pattern
const matchesPattern = (entry: DebugEntry, pattern: string): boolean => {
  const lowerPattern = pattern.toLowerCase();
  return (
    entry.name.toLowerCase().includes(lowerPattern) ||
    entry.topic.toLowerCase().includes(lowerPattern) ||
    (entry.method?.toLowerCase().includes(lowerPattern) ?? false)
  );
};

// Helper to get entry filter status
export const getEntryFilterStatus = (
  entry: DebugEntry,
  group: FilterGroup | undefined,
): "included" | "excluded" | "none" => {
  if (!group) return "none";

  for (const pattern of group.includes) {
    if (matchesPattern(entry, pattern)) return "included";
  }

  for (const pattern of group.excludes) {
    if (matchesPattern(entry, pattern)) return "excluded";
  }

  return "none";
};

// Helper to get filtered entries based on all filters
export const getFilteredEntries = (
  state: GatewayDebuggerState,
): DebugEntry[] => {
  let filtered = state.entries;
  const activeGroup = getActiveFilterGroup(state);

  // Filter by types (empty = all)
  if (activeGroup && activeGroup.types.length > 0) {
    filtered = filtered.filter((e) => activeGroup.types.includes(e.type));
  }

  if (activeGroup?.topic) {
    const topicLower = activeGroup.topic.toLowerCase();
    filtered = filtered.filter((e) =>
      e.topic.toLowerCase().includes(topicLower),
    );
  }

  if (activeGroup?.search) {
    const searchLower = activeGroup.search.toLowerCase();
    filtered = filtered.filter(
      (e) =>
        e.name.toLowerCase().includes(searchLower) ||
        e.topic.toLowerCase().includes(searchLower) ||
        JSON.stringify(e.data).toLowerCase().includes(searchLower),
    );
  }

  // Apply display mode filter
  if (activeGroup && state.displayMode !== "all") {
    filtered = filtered.filter((entry) => {
      const status = getEntryFilterStatus(entry, activeGroup);
      if (state.displayMode === "visible") {
        return status !== "excluded";
      } else if (state.displayMode === "included") {
        return status === "included";
      } else if (state.displayMode === "excluded") {
        return status === "excluded";
      }
      return true;
    });
  }

  return filtered;
};

// Helper to combine request+response entries
export const getCombinedEntries = (entries: DebugEntry[]): DebugEntry[] => {
  const combined: DebugEntry[] = [];
  const processedRequestIds = new Set<string>();

  for (const entry of entries) {
    if (entry.type === "response" && entry.requestId) {
      combined.push(entry);
      processedRequestIds.add(entry.requestId);
      continue;
    }

    if (
      entry.type === "request" &&
      entry.requestId &&
      processedRequestIds.has(entry.requestId)
    ) {
      continue;
    }

    combined.push(entry);
  }

  return combined;
};
